import React, {CSSProperties, useContext} from 'react'
import {ACTIVITY_TYPE, AllActivityTypes, AllActivityTypesArray} from "../../../../types-enums/activity";
import {DelayedSpinner, Icon, IconButton, Text} from "atmosfair-ui";
import {useNavigate} from "react-router-dom";
import {PATH_SCREEN_STRING, PATH_STRINGS} from "../../../../routes/route-props";
import './styles.css'
import clsx from "clsx";
import {useTranslation} from "react-i18next";
import {JourneyDayCardContext} from "../../common";
import {EDIT_ACTIVITY} from "../../../../types-enums";
import {renderActivityForJourney} from "../../create-journey-from-travel-components/common";
import {useDraggable} from "@dnd-kit/core";
import {DELAY_TIME_FOR_SPINNER} from "../../../../utils/common";
import {Tooltip} from "@mui/material";
import {generateTranslatedActivityTypeString} from "../../../../utils/string/common";
import {
    COMMON_COLOR,
    COMMON_THEME_COLOR,
    getStartOrEndDayOfGroupedActivity,
    START_OR_END_OF_GROUP,
    treatActivityOnMoveLikeGroupedActivity
} from "../common";
import {i18n} from "i18next";
import {groupActivities} from "../../../../utils/check/activities";

export interface JourneyOverviewCardListItemProps {
    activity: AllActivityTypes,
    highlight: boolean,
    loading: boolean,
    daysOfGroup: number[]
}

export const ICON_SIZE_CARD_LIST_ITEM = "small"
export const ICON_SIZE_DND_ICON = "small"


/**
 * Helper function to create tooltip text. Distinguishes between three states
 * 1. If the Activity has a group and context actions via pressing the alt-key are available
 * 2. If alt-key is pressed and the Activity has a group
 * 3. If the Activity can only be i.e. edited/moved/... in a group, because its car/bus Activity with a route and
 * duration > 1 day
 */
const setCommonTooltipText = (
    activity: AllActivityTypes,
    moveMoreThanOneActivity: boolean,
    altKeyPressed: boolean,
    i18n: i18n
) => {
    const {t} = i18n
    const {common: {activityType, groupId}} = activity
    const translatedActivityTypeString = generateTranslatedActivityTypeString(activityType, i18n)
    let arrowDownTooltipText = ''
    let arrowUpTooltipText = ''
    let deleteTooltipText = ''
    let editTooltipText = ''
    let baseDeleteTooltipText = t("TOOLTIP.COMMON.DELETE_TRAVEL_COMPONENT")
    let baseEditTooltipText = t("TOOLTIP.COMMON.EDIT_TRAVEL_COMPONENT")
    if (groupId) {
        deleteTooltipText = `${baseDeleteTooltipText}. ${t("TOOLTIP.COMMON.DELETE_ALT_KEY")}`
        editTooltipText = `${baseEditTooltipText}. ${t("TOOLTIP.COMMON.EDIT_ALT_KEY")}`
        const translatedText = t("TOOLTIP.COMMON.MOVE_ALT_KEY")
        arrowDownTooltipText = translatedText
        arrowUpTooltipText = translatedText
    }

    if (moveMoreThanOneActivity && groupActivities(activityType)) {
        switch (activityType) {
            case ACTIVITY_TYPE.hotel:
                if (altKeyPressed) {
                    const translatedText = t("TOOLTIP.JOURNEY.ALT_KEY_PRESSED_MOVE_ELEMENT_FROM_GROUP", {
                        activity: translatedActivityTypeString,
                    })
                    arrowDownTooltipText = translatedText
                    arrowUpTooltipText = translatedText
                    deleteTooltipText = t("TOOLTIP.JOURNEY.ALT_KEY_PRESSED_DELETE_GROUP", {
                        activity: translatedActivityTypeString,
                    })
                    editTooltipText = t("TOOLTIP.JOURNEY.ALT_KEY_PRESSED_EDIT_GROUP", {
                        activity: translatedActivityTypeString,
                    })
                }
                break;
            case ACTIVITY_TYPE.flight:
                break;
            case ACTIVITY_TYPE.car_bus:
                if (treatActivityOnMoveLikeGroupedActivity(activity)) {
                    const translatedText = t("TOOLTIP.JOURNEY.MOVE_CAR_BUS_ACTIVITY_WITH_ROUTE", {
                        interpolation: {escapeValue: false}
                    })
                    arrowDownTooltipText = translatedText
                    arrowUpTooltipText = translatedText
                    deleteTooltipText = t("TOOLTIP.JOURNEY.DELETE_CAR_BUS_ROUTE_GROUP")
                    editTooltipText = t("TOOLTIP.JOURNEY.EDIT_CAR_BUS_ROUTE_GROUP")
                } else if (altKeyPressed) {
                    const translatedText = t("TOOLTIP.JOURNEY.ALT_KEY_PRESSED_MOVE_ELEMENT_FROM_GROUP", {
                        interpolation: {escapeValue: false},
                        activity: translatedActivityTypeString,
                    })
                    arrowDownTooltipText = translatedText
                    arrowUpTooltipText = translatedText
                    deleteTooltipText = t("TOOLTIP.JOURNEY.ALT_KEY_PRESSED_DELETE_GROUP", {
                        activity: translatedActivityTypeString,
                        interpolation: {escapeValue: false}
                    })
                    editTooltipText = t("TOOLTIP.JOURNEY.ALT_KEY_PRESSED_EDIT_GROUP", {
                        activity: translatedActivityTypeString,
                        interpolation: {escapeValue: false}
                    })
                } else {
                }
                break;
            default:
                activityType satisfies never;
        }
    }

    return {arrowDownTooltipText, arrowUpTooltipText, deleteTooltipText, editTooltipText}
}

/**
 * Conditionally disables arrow down and arrow up, depending on the type of Activity and if the Activity has a group and
 * alt-key is pressed, or if the Activity like car/bus must always be moved in group because it has a route.
 * It also creates the tooltips for all the buttons available for an Activity. The content of the tooltip depends on the
 * Activity and its properties like if it can be grouped as well as the user interaction, which means, if the user has
 * the alt-key pressed.
 */
const getArrowButtonStates = (
    activity: AllActivityTypes,
    journeyDurationInDays: number | undefined,
    day: number | undefined,
    altKeyPressed: boolean,
    allActivities: AllActivityTypesArray,
    i18n: i18n
) => {
    const {t} = i18n
    let disableArrowUpFirstDayOfJourney = day === 1
    let disableArrowUpGroupedActivity = false
    let disableArrowDownLastDayOfJourney = day === journeyDurationInDays
    let disableArrowDownGroupedActivity = false
    let disableArrowDownHotelSecondLastDayOfJourney = false
    let arrowUpTooltipBaseText = ""
    let arrowDownTooltipBaseText = ""
    let arrowUpAdditionalTooltipText = ""
    let arrowDownAdditionalTooltipText = ""
    const {common: {groupId, activityType}} = activity
    let moveMoreThanOneActivity = groupId || treatActivityOnMoveLikeGroupedActivity(activity)
    if (altKeyPressed) moveMoreThanOneActivity = false
    if (typeof journeyDurationInDays !== 'undefined') {
        if (groupId) {
            const endOfActivity = getStartOrEndDayOfGroupedActivity(groupId, allActivities, START_OR_END_OF_GROUP.end)
            const startOfActivity = getStartOrEndDayOfGroupedActivity(groupId, allActivities, START_OR_END_OF_GROUP.start)
            disableArrowUpGroupedActivity = startOfActivity === 1
            disableArrowDownGroupedActivity = endOfActivity === (journeyDurationInDays - 1)
        } else {
            switch (activityType) {
                case ACTIVITY_TYPE.flight:
                    break;
                case ACTIVITY_TYPE.car_bus:
                    break;
                case ACTIVITY_TYPE.hotel:
                    disableArrowDownHotelSecondLastDayOfJourney = day === journeyDurationInDays - 1
                    break;
                default:
                    activityType satisfies never;
            }
        }
    }

    const arrowDownDisabled = disableArrowDownLastDayOfJourney || disableArrowDownGroupedActivity || disableArrowDownHotelSecondLastDayOfJourney
    const arrowUpDisabled = disableArrowUpFirstDayOfJourney || disableArrowUpGroupedActivity
    const translatedActivityTypeString = generateTranslatedActivityTypeString(activityType, i18n)
    const {
        arrowUpTooltipText,
        arrowDownTooltipText,
        deleteTooltipText,
        editTooltipText
    } = setCommonTooltipText(activity, Boolean(moveMoreThanOneActivity), altKeyPressed, i18n)
    if (arrowDownDisabled) {
        if (disableArrowDownHotelSecondLastDayOfJourney) arrowDownAdditionalTooltipText = t("HINTS.JOURNEY.ARROW_DOWN_HOTEL_ACTIVITY_DISABLED")
        if (disableArrowDownGroupedActivity) arrowDownAdditionalTooltipText = t("TOOLTIP.JOURNEY.ARROW_GROUP_EXCEEDS_JOURNEY_DURATION", {
            activity: translatedActivityTypeString,
            interpolation: {escapeValue: false}
        })
    } else {
        arrowDownTooltipBaseText = t("TOOLTIP.COMMON.MOVE_ONE_DAY_DOWN")
        arrowDownAdditionalTooltipText = arrowDownTooltipText
    }


    if (arrowUpDisabled) {
        arrowUpAdditionalTooltipText = t("TOOLTIP.JOURNEY.ARROW_ACTIVITY_EXCEEDS_JOURNEY_DURATION", {
            activity: translatedActivityTypeString,
            interpolation: {escapeValue: false}
        })
        if (moveMoreThanOneActivity) arrowUpAdditionalTooltipText = t("TOOLTIP.JOURNEY.ARROW_GROUP_EXCEEDS_JOURNEY_DURATION", {
            activity: translatedActivityTypeString,
            interpolation: {escapeValue: false}
        })
    } else {
        arrowUpTooltipBaseText = t("TOOLTIP.COMMON.MOVE_ONE_DAY_UP")
        arrowUpAdditionalTooltipText = arrowUpTooltipText
    }


    const arrowUpTooltip = `${arrowUpTooltipBaseText} ${arrowUpAdditionalTooltipText}`
    const arrowDownTooltip = `${arrowDownTooltipBaseText} ${arrowDownAdditionalTooltipText}`

    return {arrowDownDisabled, arrowUpDisabled, arrowUpTooltip, arrowDownTooltip, deleteTooltipText, editTooltipText}
}

const JourneyDayCardItem = ({
                                activity,
                                highlight,
                                loading,
                                daysOfGroup
                            }: JourneyOverviewCardListItemProps) => {
    const {
        allActivities,
        onMouseEnter,
        onMouseLeave,
        onActivityArrowUp,
        onActivityArrowDown,
        onActivityDelete,
        day,
        hasExactDates,
        droppableZoneId,
        altKeyPressed,
        journeyDurationInDays,
        draggingActivity,
    } = useContext(JourneyDayCardContext)
    const {common: {activityType, id: activityId, groupId}} = activity
    const {attributes, listeners, setNodeRef} = useDraggable({
        id: activityId,
        disabled: loading,
        data: {droppableZoneId, activity},
    })
    const navigate = useNavigate()
    const {ActivityStringComponent, EmissionsCo2KgEComponent} = renderActivityForJourney(
        activity,
        allActivities,
        hasExactDates,
        COMMON_COLOR,
        loading,
        COMMON_THEME_COLOR,
        true,
        false
    )
    const {t, i18n} = useTranslation()
    const {
        arrowDownDisabled,
        arrowUpDisabled,
        arrowDownTooltip,
        arrowUpTooltip,
        editTooltipText,
        deleteTooltipText
    } = getArrowButtonStates(
        activity,
        journeyDurationInDays,
        day,
        altKeyPressed,
        allActivities,
        i18n
    )

    const _hideActivityWhenDragging = (): CSSProperties => {
        if (draggingActivity) {
            const {common: {groupId}} = draggingActivity
            if (
                (
                    groupId &&
                    activity.common.groupId === groupId
                ) ||
                treatActivityOnMoveLikeGroupedActivity(draggingActivity)) return {display: "none"}
        }
        return {}
    }

    const _editInBulkOrSingle = () => {
        if (altKeyPressed) return EDIT_ACTIVITY.single
        if (highlight) return EDIT_ACTIVITY.bulk
        if (groupId) return EDIT_ACTIVITY.bulk
        return EDIT_ACTIVITY.single
    }

    const _renderContent = () => <div
        {...attributes}
        {...listeners}
        ref={setNodeRef}
        style={_hideActivityWhenDragging()}
        key={activityId}
        className={clsx(
            "journey-day-card-item-travel-option-selection-wrapper",
            "journey-day-card-item-travel-option-selection-wrapper-padding",
            !draggingActivity && highlight && !altKeyPressed && "journey-day-card-item-travel-option-selection-wrapper-highlight"
        )}>
        <div
            className={'journey-day-card-item-activity-button-wrapper'}>
            {loading &&
                <DelayedSpinner
                    fullscreen={false}
                    delayMs={DELAY_TIME_FOR_SPINNER}
                    size={10}
                    color={COMMON_COLOR}/>}
            <Icon
                containerClasses={"journey-day-card-item-grab-icon"}
                icon={"grip"}
                color={COMMON_COLOR}
                size={ICON_SIZE_DND_ICON}/>
            {ActivityStringComponent}
        </div>
        <div
            onMouseEnter={() => onMouseEnter(groupId ?? activityId)}
            onMouseLeave={() => onMouseLeave(groupId ?? activityId)}
            className={'journey-day-card-item-button-co2-wrapper'}>
            {EmissionsCo2KgEComponent}
            <div
                className={"journey-day-card-item-activity-button-wrapper"}>
                <IconButton
                    disabled={arrowUpDisabled || loading}
                    onTooltipOpen={() => onMouseEnter(groupId)}
                    onTooltipClose={() => onMouseLeave(groupId)}
                    tooltip={arrowUpTooltip}
                    size={ICON_SIZE_CARD_LIST_ITEM}
                    color={COMMON_COLOR}
                    icon={'arrowUp'}
                    onClick={() => onActivityArrowUp && onActivityArrowUp(activity)}/>
                <IconButton
                    disabled={arrowDownDisabled || loading}
                    onTooltipOpen={() => onMouseEnter(groupId ?? activityId)}
                    onTooltipClose={() => onMouseLeave(groupId ?? activityId)}
                    tooltip={arrowDownTooltip}
                    size={ICON_SIZE_CARD_LIST_ITEM}
                    color={COMMON_COLOR}
                    icon={'arrowDown'}
                    onClick={() => onActivityArrowDown(activity)}/>
                <IconButton
                    disabled={loading}
                    tooltip={deleteTooltipText}
                    size={ICON_SIZE_CARD_LIST_ITEM}
                    color={COMMON_COLOR}
                    icon={'trash'}
                    onClick={() => onActivityDelete(activity)}/>
                <IconButton
                    disabled={loading}
                    tooltip={editTooltipText}
                    size={ICON_SIZE_CARD_LIST_ITEM}
                    color={COMMON_COLOR}
                    icon={'penToSquare'}
                    onClick={() => navigate(`${PATH_STRINGS.editActivity}/${PATH_SCREEN_STRING.journey}/${activityType}/${activityId}/${day}/${_editInBulkOrSingle()}`)}/>
            </div>
        </div>
    </div>

    return highlight && !altKeyPressed ?
        <Tooltip placement={"top"}
                 title={<Text color={COMMON_COLOR} variant={"headlineSmall"}>{t("TOOLTIP.JOURNEY.GROUP_DURATION", {
                     activity: generateTranslatedActivityTypeString(activity.common.activityType, i18n),
                     days: daysOfGroup.join(", "),
                     interpolation: {
                         escapeValue: false
                     }
                 })}</Text>}>{_renderContent()}</Tooltip> : _renderContent()
}

export default JourneyDayCardItem